import * as L from 'leaflet';
import {
    nextTick, onUnmounted, ref, shallowRef, watch, type Ref,
} from 'vue';

/** Pilot municipality; used whenever there is nothing to fit the view to. */
export const DEFAULT_MAP_CENTER: L.LatLngTuple = [50.3535, 15.9211];
export const DEFAULT_MAP_ZOOM = 14;
const SINGLE_POINT_ZOOM = 17;

type LeafletMapOptionsType = {
    center?: L.LatLngTuple;
    zoom?: number;

    /** Tile opacity 0..1; fade the base map so markers stand out (overview maps use ~0.3). */
    tileOpacity?: number;
    onClick?: (latlng: L.LatLng) => void;
};

/**
 * Shared Leaflet setup (OpenStreetMap tiles, click handling, bounds helpers, teardown).
 * Call `initMap()` once the container element is rendered and visible; the map is
 * removed automatically when the owning component unmounts.
 */
export function useLeafletMap(container: Ref<HTMLElement | null>, options: LeafletMapOptionsType = {}) {
    const map = shallowRef<L.Map | null>(null);
    const tileLayer = shallowRef<L.TileLayer | null>(null);
    const isReady = ref(false);

    const initMap = (): L.Map | null => {
        if (map.value) {
            return map.value;
        }

        if (!container.value) {
            return null;
        }

        const instance = L.map(container.value, {
            center: options.center ?? DEFAULT_MAP_CENTER,
            zoom: options.zoom ?? DEFAULT_MAP_ZOOM,
        });

        tileLayer.value = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            opacity: options.tileOpacity ?? 1,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(instance);

        if (options.onClick) {
            instance.on('click', (event: L.LeafletMouseEvent) => options.onClick?.(event.latlng));
        }

        map.value = instance;
        isReady.value = true;

        return instance;
    };

    /**
     * Creates the map as soon as the container is in the DOM and runs `setup` once. Pages inside
     * `CompanyAdminLayout` mount while the splash screen still hides the layout on a full page
     * load (F5, a link opened in a new tab), so `initMap()` in `onMounted` found no container and
     * the map stayed blank.
     */
    const whenContainerReady = (setup: (instance: L.Map) => void | Promise<void>): void => {
        let isDone = false;

        watch(
            container,
            element => {
                if (isDone || !element) {
                    return;
                }

                const instance = initMap();
                if (instance) {
                    isDone = true;
                    void setup(instance);
                }
            },
            { immediate: true, flush: 'post' },
        );
    };

    const destroyMap = (): void => {
        if (map.value) {
            map.value.off();
            map.value.remove();
            map.value = null;
        }
        tileLayer.value = null;
        isReady.value = false;
    };

    /** Leaflet measures its container once; call after the container became visible or resized. */
    const invalidateSize = async (): Promise<void> => {
        await nextTick();
        map.value?.invalidateSize();
    };

    const fitToLatLngs = (latlngs: L.LatLngExpression[], fallback: L.LatLngTuple = DEFAULT_MAP_CENTER): void => {
        if (!map.value) {
            return;
        }

        if (latlngs.length === 0) {
            map.value.setView(fallback, DEFAULT_MAP_ZOOM);

            return;
        }

        if (latlngs.length === 1) {
            map.value.setView(latlngs[0], SINGLE_POINT_ZOOM);

            return;
        }

        map.value.fitBounds(L.latLngBounds(latlngs), { padding: [32, 32], maxZoom: 18 });
    };

    /** Vector marker: no image assets, so the default-icon path problem never shows up. */
    const createCircleMarker = (
        latlng: L.LatLngExpression,
        fillColor: string,
        extra: L.CircleMarkerOptions = {},
    ): L.CircleMarker =>
        L.circleMarker(latlng, {
            radius: 8,
            color: '#ffffff',
            weight: 2,
            fillColor,
            fillOpacity: 0.95,
            ...extra,
        });

    /**
     * DOM marker. `className` styles an inner element, never the icon element itself:
     * Leaflet positions that element with `transform: translate3d(...)`, so a `transform`
     * (scale on hover / selection), a border or padding on it moves the marker off its
     * point. The inner element should fill the box (`width/height: 100%`, `box-sizing: border-box`).
     */
    const createDivIcon = (html: string, className: string, size = 28, offset: L.PointTuple = [0, 0]): L.DivIcon =>
        L.divIcon({
            html: `<div class="${className}">${html}</div>`,
            className: 'buylo-map-icon',
            iconSize: [size, size],
            iconAnchor: [size / 2 - offset[0], size / 2 - offset[1]],
            popupAnchor: [offset[0], offset[1] - size / 2],
        });

    /**
     * Pixel offsets (for `createDivIcon`) that fan out markers sharing one point, e.g. bins
     * emptied together that got the same GPS fix. The markers stay on their coordinates, only
     * the icons move apart, so every one of them can be clicked at any zoom.
     */
    const spreadOffsets = (latlngs: L.LatLngTuple[], size = 28): L.PointTuple[] => {
        const groups = new Map<string, number[]>();
        latlngs.forEach(([lat, lng], index) => {
            const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
            groups.set(key, [...(groups.get(key) ?? []), index]);
        });

        const offsets: L.PointTuple[] = latlngs.map(() => [0, 0]);
        for (const indexes of groups.values()) {
            if (indexes.length < 2) {
                continue;
            }

            // Neighbours on the circle must not touch: chord length >= size.
            const radius = Math.max(size * 0.6, size / (2 * Math.sin(Math.PI / indexes.length)));
            indexes.forEach((index, position) => {
                const angle = (2 * Math.PI * position) / indexes.length - Math.PI;
                offsets[index] = [Math.round(radius * Math.cos(angle)), Math.round(radius * Math.sin(angle))];
            });
        }

        return offsets;
    };

    onUnmounted(destroyMap);

    return {
        map,
        tileLayer,
        isReady,
        initMap,
        whenContainerReady,
        destroyMap,
        invalidateSize,
        fitToLatLngs,
        createCircleMarker,
        createDivIcon,
        spreadOffsets,
    };
}
